import { useState } from "react";
import Sidebar from "../../../shared/components/Sidebar";
import PageLayout from "../../../shared/components/PageLayout";
import {
  TextField, Button, CircularProgress, FormControl,
  InputLabel, Select, MenuItem, FormHelperText, Snackbar, Alert, Paper
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { firebaseAuth, useFirebase } from "../../../context/Firebase";
import BackButton from "../../../shared/components/Backbtn";
import FirebaseBiodataRepository from "../api/FirebaseBiodataRepository";
import CreateBiodataUseCase from "../use-cases/CreateBiodataUseCase";
import ImageDropzone from "../../../shared/components/ImageDropzone";

const validate = (fields) => {
  const errs = {};
  if (!fields.fullName.trim())
    errs.fullName = "Full name is required.";
  else if (/\d/.test(fields.fullName))
    errs.fullName = "Full name must not contain numbers.";

  if (!fields.ageRange.trim())
    errs.ageRange = "Age is required.";
  else if (!/^\d+$/.test(fields.ageRange.trim()))
    errs.ageRange = "Age must be a number.";
  else if (Number(fields.ageRange) < 1 || Number(fields.ageRange) > 120)
    errs.ageRange = "Please enter a valid age.";

  if (!fields.caste.trim())
    errs.caste = "Caste is required.";

  if (!fields.height.trim())
    errs.height = "Height is required.";

  if (!fields.dob)
    errs.dob = "Date of birth is required.";

  if (!fields.gender)
    errs.gender = "Please select a gender.";

  if (!fields.income.trim())
    errs.income = "Income is required.";

  if (!fields.qualification.trim())
    errs.qualification = "Qualification is required.";

  if (!fields.workplace.trim())
    errs.workplace = "Workplace is required.";

  if (!fields.maritalStatus)
    errs.maritalStatus = "Please select a marital status.";

  return errs;
};

const AddBiodata = () => {
  const navigate = useNavigate();
  const [authUser] = useAuthState(firebaseAuth);
  const { getUserFromFirestore } = useFirebase();

  const [fields, setFields] = useState({
    fullName: "", ageRange: "", caste: "", height: "",
    dob: "", gender: "", income: "", qualification: "",
    workplace: "", maritalStatus: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [selectedImageFiles, setSelectedImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const biodataRepository = new FirebaseBiodataRepository();
  const createBiodataUseCase = new CreateBiodataUseCase(biodataRepository);

  const set = (field) => (e) => {
    const value = e.target.value;
    setFields((prev) => ({ ...prev, [field]: value }));
    // Validate on change if already touched
    if (touched[field]) {
      const errs = validate({ ...fields, [field]: value });
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  const blur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validate(fields);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  const handleSubmit = async () => {
    // Touch all fields to show errors
    const allTouched = Object.keys(fields).reduce((acc, k) => ({ ...acc, [k]: true }), {});
    setTouched(allTouched);
    const errs = validate(fields);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    try {
      let creatorName = "Unknown";
      if (authUser?.uid) {
        const userDoc = await getUserFromFirestore(authUser.uid);
        if (userDoc.exists()) creatorName = userDoc.data().name || authUser.email || "Unknown";
      }
      await createBiodataUseCase.execute({
        ...fields,
        dob: fields.dob ? new Date(fields.dob) : null,
        status: "Created",
        imageFiles: selectedImageFiles,
        createdBy: creatorName,
      });
      setSnackbar({ open: true, message: "Profile created successfully!", severity: "success" });
      setTimeout(() => navigate("/biodata"), 1500);
    } catch (error) {
      console.error("Error creating biodata:", error);
      setSnackbar({ open: true, message: `Failed to create profile: ${error.message}`, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const f = fields;
  const e = errors;

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="dashboard--content">
        <PageLayout title="Create Profile" actions={<BackButton tooltip="Back" />}>
          <Paper sx={{ p: 4, borderRadius: 2, maxWidth: 600 }}>

          <FormControl fullWidth margin="normal">
            <TextField
              label="Full Name"
              value={f.fullName}
              onChange={set("fullName")}
              onBlur={blur("fullName")}
              error={!!e.fullName}
              helperText={e.fullName}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <TextField
              label="Age"
              value={f.ageRange}
              onChange={set("ageRange")}
              onBlur={blur("ageRange")}
              error={!!e.ageRange}
              helperText={e.ageRange}
              inputProps={{ inputMode: "numeric" }}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <TextField
              label="Caste"
              value={f.caste}
              onChange={set("caste")}
              onBlur={blur("caste")}
              error={!!e.caste}
              helperText={e.caste}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <TextField
              label="Height"
              value={f.height}
              onChange={set("height")}
              onBlur={blur("height")}
              error={!!e.height}
              helperText={e.height}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <TextField
              label="Date of Birth"
              type="date"
              value={f.dob}
              onChange={set("dob")}
              onBlur={blur("dob")}
              error={!!e.dob}
              helperText={e.dob}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal" error={!!e.gender}>
            <InputLabel>Gender</InputLabel>
            <Select
              value={f.gender}
              onChange={set("gender")}
              onBlur={blur("gender")}
              label="Gender"
            >
              <MenuItem value=""><em>Select Gender</em></MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Non-binary">Non-binary</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
              <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
            </Select>
            {e.gender && <FormHelperText>{e.gender}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth margin="normal">
            <TextField
              label="Income"
              value={f.income}
              onChange={set("income")}
              onBlur={blur("income")}
              error={!!e.income}
              helperText={e.income}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <TextField
              label="Qualification"
              value={f.qualification}
              onChange={set("qualification")}
              onBlur={blur("qualification")}
              error={!!e.qualification}
              helperText={e.qualification}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <TextField
              label="Workplace"
              value={f.workplace}
              onChange={set("workplace")}
              onBlur={blur("workplace")}
              error={!!e.workplace}
              helperText={e.workplace}
              variant="outlined"
            />
          </FormControl>

          <FormControl fullWidth margin="normal" error={!!e.maritalStatus}>
            <InputLabel>Marital Status</InputLabel>
            <Select
              value={f.maritalStatus}
              onChange={set("maritalStatus")}
              onBlur={blur("maritalStatus")}
              label="Marital Status"
            >
              <MenuItem value=""><em>Select</em></MenuItem>
              <MenuItem value="Single">Single</MenuItem>
              <MenuItem value="Married">Married</MenuItem>
              <MenuItem value="Divorced">Divorced</MenuItem>
              <MenuItem value="Widowed">Widowed</MenuItem>
            </Select>
            {e.maritalStatus && <FormHelperText>{e.maritalStatus}</FormHelperText>}
          </FormControl>

          <ImageDropzone
            selectedFiles={selectedImageFiles}
            onFilesChange={setSelectedImageFiles}
            multiple={true}
            label="Profile Images (Optional)"
          />

          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading} sx={{ mt: 3 }}>
            {loading ? <CircularProgress size={24} /> : "Create Profile"}
          </Button>
          </Paper>
        </PageLayout>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AddBiodata;
